import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import ModalTabs from './ui/ModalTabs';
import CompactSelectPicker from './ui/CompactSelectPicker';
import ShareWithTeamPanel from './ShareWithTeamPanel';
import { Share2, Mail, Link, Copy, Eye, EyeOff, Check, Trash2, Lock, Plus, X, Info, Users, MessageSquare } from 'lucide-react';
import shareService from '../services/shareService';
import { setShareToChatTarget } from '../store/uiModalStore';
import { canShareWorkspaceWithTeam } from '../utils/workspaceShareEligibility';

const EXPIRY_OPTIONS = [
  { value: null, label: 'Never' },
  { value: 1,    label: '1 day' },
  { value: 7,    label: '7 days' },
  { value: 30,   label: '30 days' },
];

const USES_OPTIONS = [
  { value: null, label: 'Unlimited' },
  { value: 1,    label: '1 use' },
];

const BASE_TABS = [
  { id: 'shareable', icon: Link, label: 'Shareable link' },
  { id: 'email',     icon: Mail, label: 'Email share' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let _rowId = 0;
const nextId = () => ++_rowId;
const emptyRow = () => ({ id: nextId(), email: '' });

const ShareLinkModal = ({
  isOpen,
  onClose,
  type,
  item,
  showToast,
  teams = [],
  workspaces = [],
  currentUserId,
  onTeamShareSuccess,
  initialTab = 'shareable',
  fixedTeam = null,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab]           = useState('shareable');

  const [emailRows, setEmailRows]           = useState([emptyRow()]);
  const [emailMessage, setEmailMessage]     = useState('');
  const [sendingEmails, setSendingEmails]   = useState(false);

  const [password, setPassword]             = useState('');
  const [showPassword, setShowPassword]     = useState(false);
  const [usePassword, setUsePassword]       = useState(false);
  const [expiresInDays, setExpiresInDays]   = useState(null);
  const [maxUses, setMaxUses]               = useState(null);
  const [loading, setLoading]               = useState(false);
  const [shareData, setShareData]           = useState(null);
  const [copied, setCopied]                 = useState(false);
  const [revoking, setRevoking]             = useState(false);

  const canTeamShare =
    type === 'workspace' &&
    (fixedTeam != null || (item && canShareWorkspaceWithTeam(item, currentUserId)));

  const tabs = useMemo(() => {
    const next = [...BASE_TABS];
    if (canTeamShare) {
      next.push({ id: 'team', icon: Users, label: t('shareWithTeam.tab', 'Team') });
    }
    return next;
  }, [canTeamShare, t]);

  const visibleTabs = useMemo(() => {
    if (canTeamShare && fixedTeam && !item?.id) {
      return tabs.filter((tab) => tab.id === 'team');
    }
    return tabs;
  }, [canTeamShare, fixedTeam, item?.id, tabs]);

  useEffect(() => {
    if (!isOpen) return;
    const defaultTab = visibleTabs.some((tab) => tab.id === initialTab)
      ? initialTab
      : visibleTabs[0]?.id || 'shareable';
    setActiveTab(defaultTab);
    setEmailRows([emptyRow()]);
    setEmailMessage('');
    setSendingEmails(false);
    setPassword('');
    setUsePassword(false);
    setShowPassword(false);
    setExpiresInDays(null);
    setMaxUses(null);
    setLoading(false);
    setShareData(null);
    setCopied(false);
    setRevoking(false);
  }, [isOpen, initialTab, visibleTabs]);

  const handleClose = () => {
    setEmailRows([emptyRow()]);
    setEmailMessage('');
    setPassword('');
    setUsePassword(false);
    setShowPassword(false);
    setExpiresInDays(null);
    setMaxUses(null);
    setLoading(false);
    setShareData(null);
    setCopied(false);
    setRevoking(false);
    onClose();
  };

  const updateEmailRow = (id, val) =>
    setEmailRows(prev => prev.map(r => r.id === id ? { ...r, email: val } : r));

  const removeEmailRow = (id) =>
    setEmailRows(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev);

  const validEmailRows = emailRows.filter(r => EMAIL_RE.test(r.email.trim()));

  const handleSendEmails = async () => {
    if (validEmailRows.length === 0) return;
    setSendingEmails(true);
    try {
      const emails = validEmailRows.map(r => r.email.trim());
      await shareService.emailShare(type, item.id, emails, emailMessage);
      showToast?.(`${validEmailRows.length} share invitation${validEmailRows.length > 1 ? 's' : ''} sent`, 'success');
      handleClose();
    } catch (err) {
      showToast?.(err.message || 'Failed to send', 'error');
    } finally {
      setSendingEmails(false);
    }
  };

  const handleGenerate = async () => {
    if (!item?.id) return;
    setLoading(true);
    try {
      const data = await shareService.createShareLink(type, item.id, {
        password: usePassword && password ? password : null,
        expiresInDays,
        maxUses,
      });
      setShareData(data);
    } catch (err) {
      showToast?.(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!shareData?.url) return;
    try {
      await navigator.clipboard.writeText(shareData.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast?.('Failed to copy to clipboard', 'error');
    }
  };

  const handleRevoke = async () => {
    if (!shareData?.token) return;
    setRevoking(true);
    try {
      await shareService.revokeShareLink(shareData.token);
      showToast?.('Share link revoked', 'success');
      setShareData(null);
    } catch (err) {
      showToast?.(err.message, 'error');
    } finally {
      setRevoking(false);
    }
  };

  const typeLabel = type === 'team' ? 'Team' : type === 'workspace' ? 'Workspace' : 'Collection';

  const subtitle =
    activeTab === 'team'
      ? fixedTeam
        ? t('shareWithTeam.subtitleForTeam', { name: fixedTeam.name })
        : item
          ? t('shareWithTeam.subtitleForWorkspace', { name: item.name })
          : t('shareWithTeam.subtitle')
      : 'Share via email or create a shareable link';

  const sectionLabel = (text) => (
    <p style={{ marginBottom: '0.5rem', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>
      {text}
    </p>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={activeTab === 'team' ? t('shareWithTeam.title') : `Share ${typeLabel}`}
      subtitle={subtitle}
      customIcon={
        activeTab === 'team'
          ? <Users size={22} className="text-violet-500" />
          : <Share2 size={22} className="text-blue-500" />
      }
    >
      {visibleTabs.length > 1 && (
        <ModalTabs tabs={visibleTabs} activeTab={activeTab} onTabChange={setActiveTab} />
      )}

      {/* Recipient-scoped alternative: hand the item to the chat share flow. */}
      {item?.id && (type === 'workspace' || type === 'collection') && (
        <button
          type="button"
          className="share-alt-btn"
          onClick={() => {
            handleClose();
            setShareToChatTarget({ type, item });
          }}
        >
          <MessageSquare size={14} />
          <span>Send in chat instead…</span>
        </button>
      )}

      <div>

        {/* ── Team share tab ────────────────────────────────── */}
        {activeTab === 'team' && canTeamShare && (
          <ShareWithTeamPanel
            team={fixedTeam}
            workspace={item}
            teams={teams}
            workspaces={workspaces}
            currentUserId={currentUserId}
            showToast={showToast}
            onSuccess={async (result) => {
              await onTeamShareSuccess?.(result);
              handleClose();
            }}
          />
        )}

        {/* ── Email share tab ───────────────────────────────── */}
        {activeTab === 'email' && item?.id && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              {sectionLabel('People to share with')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {emailRows.map(row => (
                  <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <Mail size={14} style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                      <input
                        type="email"
                        value={row.email}
                        onChange={e => updateEmailRow(row.id, e.target.value)}
                        placeholder="name@email.com"
                        className="modal-invite-input modal-input-no-focus-outline modal-input-flat"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeEmailRow(row.id)}
                      disabled={emailRows.length === 1}
                      className="modal-row-remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setEmailRows(prev => [...prev, emptyRow()])}
                className="modal-btn-ghost"
                style={{ marginTop: '0.5rem' }}
              >
                <Plus size={13} />
                Add another
              </button>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>
                Message <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
              </label>
              <textarea
                value={emailMessage}
                onChange={e => setEmailMessage(e.target.value)}
                placeholder={`Check out this ${typeLabel.toLowerCase()} — let me know if you have any questions.`}
                rows={3}
                className="modal-form-textarea modal-input-flat"
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '0.75rem', paddingTop: '0.25rem' }}>
              <p className="modal-footer-info">
                <Info size={12} />
                Recipients will receive a link to view this {typeLabel.toLowerCase()}.
              </p>
              <div style={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: '0.5rem' }}>
                <button type="button" onClick={handleClose} className="modal-btn-cancel">Cancel</button>
                <button
                  type="button"
                  onClick={handleSendEmails}
                  disabled={sendingEmails || validEmailRows.length === 0}
                  className="bg-blue-600"
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: (sendingEmails || validEmailRows.length === 0) ? 'not-allowed' : 'pointer', opacity: (sendingEmails || validEmailRows.length === 0) ? 0.5 : 1 }}
                >
                  {sendingEmails ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Shareable link tab ────────────────────────────── */}
        {activeTab === 'shareable' && item?.id && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {!shareData ? (
              <>
                <div>
                  <p className="modal-logo-label" style={{ marginBottom: '0.375rem' }}>Link expiry</p>
                  <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                    <CompactSelectPicker
                      value={expiresInDays}
                      onChange={setExpiresInDays}
                      options={EXPIRY_OPTIONS}
                      aria-label="Link expiry"
                    />
                  </div>
                </div>

                <div>
                  <p className="modal-logo-label" style={{ marginBottom: '0.375rem' }}>Link uses</p>
                  <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                    <CompactSelectPicker
                      value={maxUses}
                      onChange={setMaxUses}
                      options={USES_OPTIONS}
                      aria-label="Link uses"
                    />
                  </div>
                </div>

                <div className="settings-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }} className="modal-toggle-label">
                    <Lock size={13} style={{ flexShrink: 0 }} className="modal-toggle-icon" />
                    Password protect this link
                  </span>
                  <button
                    type="button"
                    className={`settings-toggle${usePassword ? ' settings-toggle--on' : ''}`}
                    onClick={() => setUsePassword(v => !v)}
                    aria-checked={usePassword}
                    role="switch"
                  >
                    <span className="settings-toggle-thumb" />
                  </button>
                </div>

                {usePassword && (
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter a password"
                      className="modal-input modal-input-no-focus-outline modal-input-flat"
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={loading || (usePassword && !password.trim())}
                  className="bg-blue-600"
                  style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 500, color: '#fff', border: 'none', borderRadius: '0.625rem', cursor: (loading || (usePassword && !password.trim())) ? 'not-allowed' : 'pointer', opacity: (loading || (usePassword && !password.trim())) ? 0.5 : 1, marginTop: '0.25rem' }}
                >
                  {loading ? (
                    <>
                      <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite' }} />
                      <span>Generating…</span>
                    </>
                  ) : (
                    <>
                      <Link size={16} />
                      <span>Generate Share Link</span>
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <div style={{ borderRadius: '0.625rem', border: '1px solid #bbf7d0', background: '#f0fdf4', padding: '0.75rem' }}>
                  <p style={{ marginBottom: '0.375rem', fontSize: '0.75rem', fontWeight: 500, color: '#15803d' }}>Share link ready</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="text"
                      readOnly
                      value={shareData.url}
                      className="modal-input modal-input-no-focus-outline modal-input-flat"
                      style={{ flex: 1, minWidth: 0, padding: '0.375rem 0.625rem', fontSize: '0.75rem' }}
                    />
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="bg-blue-600"
                      style={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: '0.3rem', padding: '0.375rem 0.625rem', fontSize: '0.75rem', fontWeight: 500, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}
                    >
                      {copied ? <Check size={13} /> : <Copy size={13} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', color: '#16a34a' }}>
                    {shareData.password_protected && <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}><Lock size={11} /> Password protected</span>}
                    {shareData.max_uses === 1 && <span>One-time use</span>}
                    {shareData.max_uses > 1 && <span>{shareData.max_uses} uses max</span>}
                    {shareData.expires_at && <span>Expires {new Date(shareData.expires_at).toLocaleDateString()}</span>}
                    {!shareData.expires_at && <span>Never expires</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <button
                    type="button"
                    onClick={() => setShareData(null)}
                    style={{ flex: 1, borderRadius: '0.625rem', border: '1px solid #e5e7eb', padding: '0.5625rem 1rem', fontSize: '0.875rem', fontWeight: 500, color: '#374151', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Generate New Link
                  </button>
                  <button
                    type="button"
                    onClick={handleRevoke}
                    disabled={revoking}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', borderRadius: '0.625rem', border: '1px solid #fecaca', padding: '0.5625rem 1rem', fontSize: '0.875rem', fontWeight: 500, color: '#dc2626', background: 'transparent', cursor: revoking ? 'not-allowed' : 'pointer', opacity: revoking ? 0.5 : 1, fontFamily: 'inherit' }}
                  >
                    <Trash2 size={14} />
                    {revoking ? 'Revoking…' : 'Revoke'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </Modal>
  );
};

export default ShareLinkModal;
